// src/admin/components/BatchCreateModal.jsx
import React, { useState, useEffect } from "react";
import { crearRegistrosBatch } from "../../services/api";

// Límites de caracteres para campos de autenticidad
// NOTA: numeroSerie ya NO está aquí - ahora es un campo universal generado automáticamente
const LIMITES_AUTENTICIDAD = {
  tipoProducto: 50,
  edicion: 20,
  mensaje: 100,
  fechaFabricacion: 30,
  campoPersonalizado1: 100,
  campoPersonalizado2: 100
};

// Configuración de columnas para la tabla de autenticidad
// NOTA: numeroSerie ya NO está aquí - se genera automáticamente como campo universal
const COLUMNAS_AUTENTICIDAD = [
  { key: 'tipoProducto', label: 'Tipo Producto', placeholder: 'Ej: Termo Acero' },
  { key: 'edicion', label: 'Edición', placeholder: 'Ej: 1 de 50' },
  { key: 'mensaje', label: 'Mensaje', placeholder: 'Ej: Producto original' },
  { key: 'fechaFabricacion', label: 'Fecha Fab.', placeholder: 'Ej: Nov 2024' },
  { key: 'campoPersonalizado1', label: 'Campo 1', placeholder: 'Personalizado' },
  { key: 'campoPersonalizado2', label: 'Campo 2', placeholder: 'Personalizado' }
];

function BatchCreateModal({ show, onClose, onBatchCreated }) {
  const [count, setCount] = useState(10);
  const [tipo, setTipo] = useState("mascota");
  const [plan, setPlan] = useState("light");
  const [conCodigoActivacion, setConCodigoActivacion] = useState(true);
  const [agregarAutenticidad, setAgregarAutenticidad] = useState(false);
  const [datosAutenticidad, setDatosAutenticidad] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);

  // Inicializar/actualizar filas de autenticidad cuando cambia la cantidad
  // NOTA: numeroSerie ya NO se incluye aquí - se genera automáticamente como campo universal
  useEffect(() => {
    if (tipo === 'coleccionable' && agregarAutenticidad) {
      setDatosAutenticidad(prev => {
        const newData = [...prev];
        // Agregar filas si faltan
        while (newData.length < count) {
          newData.push({
            tipoProducto: '',
            edicion: '',
            mensaje: '',
            fechaFabricacion: '',
            campoPersonalizado1: '',
            campoPersonalizado2: ''
          });
        }
        // Remover filas si sobran
        return newData.slice(0, count);
      });
    }
  }, [count, tipo, agregarAutenticidad]);

  // Resetear autenticidad si cambia el tipo
  useEffect(() => {
    if (tipo !== 'coleccionable') {
      setAgregarAutenticidad(false);
      setDatosAutenticidad([]);
    }
  }, [tipo]);

  // Actualizar un campo específico de una fila
  const handleAutenticidadChange = (index, field, value) => {
    const limite = LIMITES_AUTENTICIDAD[field];
    const valorTruncado = value.substring(0, limite);

    setDatosAutenticidad(prev => {
      const newData = [...prev];
      newData[index] = { ...newData[index], [field]: valorTruncado };
      return newData;
    });
  };

  // Copiar valor a todas las filas
  const copiarATodos = (field, valor) => {
    setDatosAutenticidad(prev =>
      prev.map(row => ({ ...row, [field]: valor }))
    );
  };

  async function handleCreateBatch() {
    setLoading(true);
    setMensaje("");

    try {
      // Preparar datos de autenticidad solo si aplica
      const autenticidadParaEnviar = (tipo === 'coleccionable' && agregarAutenticidad)
        ? datosAutenticidad
        : null;

      const csvBlob = await crearRegistrosBatch(
        count,
        tipo,
        tipo === 'coleccionable' ? 'light' : plan,
        conCodigoActivacion,
        autenticidadParaEnviar
      );

      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `batch-${tipo}-${count}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMensaje("Registros creados y CSV descargado con éxito!");
      if (onBatchCreated) onBatchCreated();
    } catch (error) {
      console.error("Error al crear en lote:", error);
      setMensaje(error.message || "Error al crear en lote");
    } finally {
      setLoading(false);
    }
  }

  if (!show) return null;

  const esColeccionable = tipo === 'coleccionable';

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className={`modal-dialog modal-dialog-centered ${esColeccionable && agregarAutenticidad ? 'modal-xl' : ''}`}
           style={{ maxWidth: esColeccionable && agregarAutenticidad ? '95%' : undefined }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-collection me-2"></i>
              Crear en Lote
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <div className="modal-body">
            {mensaje && (
              <p className={`alert ${mensaje.includes('éxito') ? 'alert-success' : 'alert-danger'}`}>
                {mensaje}
              </p>
            )}

            <div className="row">
              {/* Columna izquierda: configuración básica */}
              <div className={esColeccionable && agregarAutenticidad ? 'col-md-3' : 'col-12'}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Cantidad (1-50):</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={count}
                    onChange={(e) => setCount(Math.min(50, Math.max(1, Number(e.target.value))))}
                    className="form-control"
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Tipo de Objeto:</label>
                  <select
                    className="form-select"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    disabled={loading}
                  >
                    <option value="mascota">Mascota</option>
                    <option value="llavero">Llavero</option>
                    <option value="coleccionable">Coleccionable</option>
                  </select>
                </div>

                {/* Selector de plan - oculto para coleccionables */}
                {!esColeccionable && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Plan:</label>
                    <select
                      className="form-select"
                      value={plan}
                      onChange={(e) => setPlan(e.target.value)}
                      disabled={loading}
                    >
                      <option value="light">Light</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                )}

                {/* Info para coleccionables */}
                {esColeccionable && (
                  <div className="alert alert-info py-2 mb-3">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      Los coleccionables siempre son plan <strong>Light</strong>.
                    </small>
                  </div>
                )}

                {/* Checkbox código de activación */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="codigoActivacion"
                      checked={conCodigoActivacion}
                      onChange={(e) => setConCodigoActivacion(e.target.checked)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="codigoActivacion">
                      <strong>Generar con código de activación</strong>
                    </label>
                  </div>
                  <small className="text-muted d-block mt-1 ms-4">
                    <i className="bi bi-shield-lock me-1"></i>
                    Código rascable de 6 caracteres.
                  </small>
                </div>

                {/* Checkbox autenticidad - solo para coleccionables */}
                {esColeccionable && (
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="agregarAutenticidad"
                        checked={agregarAutenticidad}
                        onChange={(e) => setAgregarAutenticidad(e.target.checked)}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="agregarAutenticidad">
                        <strong>
                          <i className="bi bi-shield-check me-1"></i>
                          Agregar datos de autenticidad
                        </strong>
                      </label>
                    </div>
                    <small className="text-muted d-block mt-1 ms-4">
                      Certificado digital inmutable visible al escanear.
                    </small>
                  </div>
                )}
              </div>

              {/* Columna derecha: tabla de autenticidad */}
              {esColeccionable && agregarAutenticidad && (
                <div className="col-md-9">
                  <div className="border rounded p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <h6 className="mb-2">
                      <i className="bi bi-table me-1"></i>
                      Datos de Autenticidad ({count} registros)
                    </h6>
                    <div className="alert alert-success py-1 px-2 mb-2" style={{ fontSize: '0.75rem' }}>
                      <i className="bi bi-upc-scan me-1"></i>
                      <strong>Numero de Serie:</strong> Se genera automaticamente (ej: COL-000123-AF39C2)
                    </div>
                    <small className="text-muted d-block mb-2">
                      Todos los campos son opcionales. Usa "Copiar ↓" para aplicar a todos.
                    </small>

                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '40px' }}>#</th>
                            {COLUMNAS_AUTENTICIDAD.map(col => (
                              <th key={col.key} style={{ minWidth: '100px' }}>
                                <div className="d-flex flex-column">
                                  <span>{col.label}</span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-primary"
                                    style={{ fontSize: '0.7rem' }}
                                    onClick={() => {
                                      if (datosAutenticidad[0]?.[col.key]) {
                                        copiarATodos(col.key, datosAutenticidad[0][col.key]);
                                      }
                                    }}
                                    disabled={!datosAutenticidad[0]?.[col.key]}
                                    title="Copiar valor de fila 1 a todos"
                                  >
                                    Copiar ↓
                                  </button>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {datosAutenticidad.map((row, idx) => (
                            <tr key={idx}>
                              <td className="text-center align-middle fw-bold">{idx + 1}</td>
                              {COLUMNAS_AUTENTICIDAD.map(col => (
                                <td key={col.key} className="p-1">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '0.75rem' }}
                                    placeholder={col.placeholder}
                                    value={row[col.key] || ''}
                                    onChange={(e) => handleAutenticidadChange(idx, col.key, e.target.value)}
                                    maxLength={LIMITES_AUTENTICIDAD[col.key]}
                                    disabled={loading}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Botón de crear */}
            <div className="mt-3">
              <button
                className="btn btn-primary w-100"
                onClick={handleCreateBatch}
                disabled={loading || count < 1 || count > 50}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-download me-2"></i>
                    Crear {count} {tipo === 'coleccionable' ? 'Coleccionables' : tipo === 'mascota' ? 'Mascotas' : 'Llaveros'} y Descargar CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchCreateModal;
